/**
 * Salvajes · Vista previa de la identificación (placa de silicona)
 *
 * Archivo propio (no es parte de Pitch). Lo usa blocks/id-tag-preview.liquid.
 * No reemplaza nada: lee lo que la persona carga en los campos de la app de
 * opciones (Easify: "Nombre", "Tipo de letra") y el color de la variante, y dibuja
 * la placa con el nombre en la tipografía elegida. El grabado es siempre negro.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';
const norm = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

class IdTagPreview extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    const raw = this.querySelector('script[data-idp]');
    if (!raw) return;
    this.data = JSON.parse(raw.textContent);
    this.fonts = this.parseFonts(this.data.fontsText);
    this.colorHex = this.parseColors(this.data.colorsText);
    this.refs = {
      svg: this.querySelector('[data-ref="svg"]'),
      count: this.querySelector('[data-ref="count"]'),
      fontLabel: this.querySelector('[data-ref="font"]'),
    };
    this.state = { name: null, font: null, color: null };
    this.build();
    this.tick = this.tick.bind(this);
    // La app de opciones cambia valores por código (sin eventos fiables):
    // revisamos cada 250 ms y redibujamos solo si algo cambió.
    this.timer = setInterval(this.tick, 250);
    // Las fuentes de Adobe llegan después: cada vez que termina de cargar una, volvemos a medir.
    this.refit = () => this.fit();
    if (document.fonts) {
      document.fonts.addEventListener?.('loadingdone', this.refit);
      document.fonts.ready.then(this.refit);
    }
    ['input', 'change', 'click'].forEach((ev) => document.addEventListener(ev, this.tick, true));
    this.tick();
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    document.fonts?.removeEventListener?.('loadingdone', this.refit);
    ['input', 'change', 'click'].forEach((ev) => document.removeEventListener(ev, this.tick, true));
  }

  /* ------------------------------------------------------------ config */

  // "Opción = familia | peso"  ·  peso puede ser "wght 600" para fuentes variables
  parseFonts(txt) {
    const map = new Map();
    String(txt || '')
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .forEach((line) => {
        const [label, rest] = line.split('=').map((s) => (s || '').trim());
        if (!label || !rest) return;
        const [family, weightRaw] = rest.split('|').map((s) => (s || '').trim());
        const w = weightRaw || '400';
        const variable = /^wght\s+(\d+)/i.exec(w);
        map.set(norm(label), {
          label,
          family,
          weight: variable ? Number(variable[1]) : Number(w) || 400,
          variation: variable ? `"wght" ${variable[1]}` : null,
        });
      });
    return map;
  }

  parseColors(txt) {
    const map = new Map();
    String(txt || '')
      .split(/\n|,/)
      .map((l) => l.split(':').map((x) => x.trim()))
      .filter((p) => p.length === 2 && /^#?[0-9a-f]{3,8}$/i.test(p[1]))
      .forEach(([name, hex]) => map.set(norm(name), hex.startsWith('#') ? hex : `#${hex}`));
    return map;
  }

  /* ------------------------------------------------------- leer la página */

  form() {
    return (
      this.closest('product-form-component, form') ||
      document.querySelector(`form[action*="/cart/add"] [name="id"]`)?.closest('form') ||
      document
    );
  }

  readName() {
    const field = this.data.nameField || 'Nombre';
    const el = document.querySelector(`[name="properties[${field}]"]`);
    if (el && this.data.maxChars && el.maxLength !== this.data.maxChars) {
      el.maxLength = this.data.maxChars; // tope de caracteres que se pueden grabar
    }
    return el ? el.value : '';
  }

  readFont() {
    const field = this.data.fontField || 'Tipo de letra';
    const radio = [...document.querySelectorAll('input[type="radio"]:checked')].find((r) =>
      (r.name || '').endsWith(`,${field}`)
    );
    if (radio) return radio.value;
    const sel = [...document.querySelectorAll('select')].find((s) => (s.name || '').endsWith(`,${field}`));
    if (sel) return sel.value;
    return document.querySelector(`[name="properties[${field}]"]`)?.value || '';
  }

  readColor() {
    const d = this.data;
    // 1) variante seleccionada en el formulario del producto
    const idInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
    const id = idInput && String(idInput.value);
    if (id && d.variants[id]) return d.variants[id];
    // 2) radio del selector de variantes
    const opt = d.colorOption || 'Color';
    const r = [...document.querySelectorAll('variant-picker input[type="radio"]:checked')].find((x) =>
      (x.name || '').toLowerCase().startsWith(opt.toLowerCase())
    );
    if (r) return r.value;
    // 3) ?variant= en la URL
    const urlId = new URLSearchParams(location.search).get('variant');
    if (urlId && d.variants[urlId]) return d.variants[urlId];
    return d.defaultColor;
  }

  tick() {
    const name = this.readName();
    const font = this.readFont();
    const color = this.readColor();
    if (name === this.state.name && font === this.state.font && color === this.state.color) return;
    this.state = { name, font, color };
    this.render();
  }

  /* --------------------------------------------------------------- colores */

  hexFor(color) {
    const k = norm(color);
    if (this.colorHex.has(k)) return Promise.resolve(this.colorHex.get(k));
    const info = this.data.colorInfo[color] || {};
    const fallback = info.sw || '#d9d0c8';
    if (!info.img) return Promise.resolve(fallback);
    this._pending = this._pending || new Map();
    if (this._pending.has(k)) return this._pending.get(k);
    const p = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const W = Math.min(200, img.naturalWidth);
          const H = Math.max(1, Math.round((W * img.naturalHeight) / img.naturalWidth));
          const c = document.createElement('canvas');
          c.width = W;
          c.height = H;
          const x = c.getContext('2d', { willReadFrequently: true });
          x.drawImage(img, 0, 0, W, H);
          const px = x.getImageData(0, 0, W, H).data;
          const bg = [px[0], px[1], px[2]];
          const all = [];
          const light = [];
          for (let i = 0; i < px.length; i += 4) {
            const q = [px[i], px[i + 1], px[i + 2]];
            if (Math.hypot(q[0] - bg[0], q[1] - bg[1], q[2] - bg[2]) < 40) continue;
            all.push(q);
            if (0.2126 * q[0] + 0.7152 * q[1] + 0.0722 * q[2] > 70) light.push(q); // sin el negro del grabado
          }
          if (!all.length) return resolve(fallback);
          const set = light.length > all.length * 0.4 ? light : all;
          const med = (k2) => set.map((q) => q[k2]).sort((a, b) => a - b)[set.length >> 1];
          resolve('#' + [0, 1, 2].map((k2) => med(k2).toString(16).padStart(2, '0')).join(''));
        } catch (_) {
          resolve(fallback);
        }
      };
      img.onerror = () => resolve(fallback);
      img.src = info.img;
    }).then((hex) => {
      this.colorHex.set(k, hex);
      return hex;
    });
    this._pending.set(k, p);
    return p;
  }

  /* ---------------------------------------------------------------- dibujo */

  el(tag, attrs = {}) {
    const n = document.createElementNS(SVG_NS, tag);
    for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, String(v));
    return n;
  }

  build() {
    const svg = this.refs.svg;
    svg.setAttribute('viewBox', '0 0 240 240');
    const uid = `idp${Math.random().toString(36).slice(2, 7)}`;
    const defs = this.el('defs');
    const shade = this.el('radialGradient', { id: `${uid}-sh`, cx: '38%', cy: '30%', r: '75%' });
    shade.appendChild(this.el('stop', { offset: '0', 'stop-color': '#fff', 'stop-opacity': 0.28 }));
    shade.appendChild(this.el('stop', { offset: '0.55', 'stop-color': '#fff', 'stop-opacity': 0 }));
    shade.appendChild(this.el('stop', { offset: '1', 'stop-color': '#000', 'stop-opacity': 0.14 }));
    defs.appendChild(shade);
    const drop = this.el('filter', { id: `${uid}-ds`, x: '-20%', y: '-20%', width: '140%', height: '150%' });
    drop.appendChild(this.el('feDropShadow', { dx: 0, dy: 5, stdDeviation: 5, 'flood-color': '#3a1d10', 'flood-opacity': 0.25 }));
    defs.appendChild(drop);
    svg.appendChild(defs);

    const g = this.el('g', { filter: `url(#${uid}-ds)` });
    // base negra (es lo que deja ver el láser) + cara de color
    g.appendChild(this.el('circle', { cx: 120, cy: 120, r: 112, fill: '#1b1a19' }));
    this.face = this.el('circle', { cx: 120, cy: 120, r: 105, fill: '#d9d0c8' });
    g.appendChild(this.face);
    g.appendChild(this.el('circle', { cx: 120, cy: 120, r: 105, fill: `url(#${uid}-sh)` }));
    svg.appendChild(g);

    // Teléfono de ejemplo alrededor del nombre, como en la placa real:
    // arriba queda "dado vuelta" (sigue el círculo) y abajo se lee derecho.
    const phone = (this.data.phoneExample || '').trim();
    if (phone) {
      const R = 86;
      const top = this.el('path', { id: `${uid}-top`, d: `M ${120 + R} 120 A ${R} ${R} 0 0 0 ${120 - R} 120`, fill: 'none' });
      const bottom = this.el('path', { id: `${uid}-bot`, d: `M ${120 - R} 120 A ${R} ${R} 0 0 0 ${120 + R} 120`, fill: 'none' });
      defs.appendChild(top);
      defs.appendChild(bottom);
      const ring = (ref) => {
        const t = this.el('text', {
          'font-size': 17,
          'font-weight': 700,
          'letter-spacing': 1.2,
          fill: '#1b1a19',
          'fill-opacity': this.data.phoneOpacity ?? 1,
          class: 'idp__phone',
        });
        const tp = this.el('textPath', { href: `#${ref}`, startOffset: '50%', 'text-anchor': 'middle' });
        tp.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', `#${ref}`);
        tp.textContent = phone;
        t.appendChild(tp);
        svg.appendChild(t);
      };
      ring(`${uid}-top`);
      ring(`${uid}-bot`);
    }

    this.text = this.el('text', {
      x: 120,
      y: 121,
      'text-anchor': 'middle',
      'dominant-baseline': 'central',
      fill: '#1b1a19',
    });
    svg.appendChild(this.text);
  }

  render() {
    const { name, font, color } = this.state;
    const empty = !name;
    const fontCfg = this.fonts.get(norm(font)) || this.fonts.get(norm(this.data.defaultFont)) || [...this.fonts.values()][0];

    // color de la placa
    this.hexFor(color).then((hex) => {
      if (this.state.color === color) this.face.setAttribute('fill', hex);
    });

    // texto
    const t = this.text;
    t.textContent = empty ? this.data.placeholder || 'Lucie' : name;
    t.setAttribute('fill-opacity', empty ? 0.28 : 1);
    if (fontCfg) {
      t.style.fontFamily = `"${fontCfg.family}", sans-serif`;
      t.style.fontWeight = String(fontCfg.weight);
      t.style.fontVariationSettings = fontCfg.variation || 'normal';
    }
    this.fit();
    // cuando termina de cargar la tipografía, volvemos a medir (y por las dudas, un par de veces más)
    if (fontCfg && document.fonts && document.fonts.load) {
      document.fonts
        .load(`${fontCfg.weight} 40px "${fontCfg.family}"`, t.textContent)
        .then(() => this.fit())
        .catch(() => {});
    }
    clearTimeout(this._fitA);
    clearTimeout(this._fitB);
    this._fitA = setTimeout(this.refit || (() => this.fit()), 300);
    this._fitB = setTimeout(this.refit || (() => this.fit()), 1200);

    const n = Array.from(name || '').length;
    if (this.refs.count) this.refs.count.textContent = `${n}/${this.data.maxChars}`;
    if (this.refs.fontLabel) this.refs.fontLabel.textContent = fontCfg && font ? fontCfg.label : 'Elegí una tipografía';
    this.classList.toggle('is-empty', empty);
  }

  /** Achica el texto hasta que entra en la franja central de la placa. */
  fit() {
    const t = this.text;
    const maxW = 132;
    const maxH = 54;
    let size = 64;
    t.setAttribute('font-size', size);
    try {
      const b = t.getBBox();
      if (b.width > 0) {
        const k = Math.min(1, maxW / b.width, maxH / b.height);
        size = Math.max(14, Math.floor(size * k));
        t.setAttribute('font-size', size);
      }
    } catch (_) {}
  }
}

if (!customElements.get('id-tag-preview')) customElements.define('id-tag-preview', IdTagPreview);